import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getAccountDashboards,
  createDashboard,
  updateDashboard,
  deleteDashboard,
  getDashboardCharts,
  createChartConfig,
  updateChartConfig,
  deleteChartConfig,
  fetchChartData,
  subscribeToDashboards,
  getChartTypes,
  getDefaultChartColors,
  exportDashboardConfig,
  duplicateDashboard
} from '../services/dashboard';
import { getAccountQueryTemplates } from '../services/dataQuery';
import './DashboardBuilder.css';

function DashboardBuilder() {
  const navigate = useNavigate();
  const { accountId } = useParams();
  const [dashboards, setDashboards] = useState([]);
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const [charts, setCharts] = useState([]);
  const [chartData, setChartData] = useState({});
  const [queryTemplates, setQueryTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDashboardModal, setShowDashboardModal] = useState(false);
  const [showChartModal, setShowChartModal] = useState(false);
  const [editingDashboard, setEditingDashboard] = useState(null);
  const [editingChart, setEditingChart] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const chartTypes = getChartTypes();
  const defaultColors = getDefaultChartColors();

  // Dashboard form state
  const [dashboardForm, setDashboardForm] = useState({
    name: '',
    description: '',
    refreshInterval: 60
  });

  // Chart form state
  const [chartForm, setChartForm] = useState({
    name: '',
    type: 'bar',
    dataSource: {
      type: 'query',
      queryTemplateId: ''
    },
    config: {
      xAxisField: '',
      yAxisField: '',
      labelField: '',
      valueField: '',
      colors: defaultColors,
      showLegend: true,
      showGrid: true,
      title: '',
      subtitle: ''
    },
    limit: 10
  });

  useEffect(() => {
    if (accountId) {
      loadData();

      const unsubscribe = subscribeToDashboards(accountId, (dashboardsData) => {
        setDashboards(dashboardsData);
      });

      return () => unsubscribe();
    } else {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    if (selectedDashboard) {
      loadDashboardCharts(selectedDashboard.id);
    }
  }, [selectedDashboard]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [dashboardsData, templatesData] = await Promise.all([
        getAccountDashboards(accountId),
        getAccountQueryTemplates(accountId)
      ]);

      setDashboards(dashboardsData);
      setQueryTemplates(templatesData);
    } catch (err) {
      console.error('Error loading data:', err);
      alert('載入失敗：' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadDashboardCharts = async (dashboardId) => {
    try {
      const chartsData = await getDashboardCharts(dashboardId);
      setCharts(chartsData);

      // Load data for each chart
      const dataPromises = chartsData.map(async (chart) => {
        try {
          const data = await fetchChartData(chart);
          return { chartId: chart.id, data };
        } catch (err) {
          console.error(`Error loading data for chart ${chart.id}:`, err);
          return { chartId: chart.id, data: [] };
        }
      });

      const results = await Promise.all(dataPromises);
      const newChartData = {};
      results.forEach(({ chartId, data }) => {
        newChartData[chartId] = data;
      });
      setChartData(newChartData);
    } catch (err) {
      console.error('Error loading dashboard charts:', err);
      alert('載入圖表失敗：' + err.message);
    }
  };

  const handleCreateDashboard = async (e) => {
    e.preventDefault();

    if (!dashboardForm.name.trim()) {
      alert('請輸入儀表板名稱');
      return;
    }

    try {
      const dashboard = await createDashboard({
        ...dashboardForm,
        accountId: accountId || 'default'
      });
      alert('儀表板已創建');
      resetDashboardForm();
      setShowDashboardModal(false);
      setSelectedDashboard(dashboard);
    } catch (err) {
      console.error('Error creating dashboard:', err);
      alert('創建失敗：' + err.message);
    }
  };

  const handleUpdateDashboard = async (e) => {
    e.preventDefault();

    if (!editingDashboard) return;

    try {
      await updateDashboard(editingDashboard.id, dashboardForm);
      alert('儀表板已更新');
      resetDashboardForm();
      setEditingDashboard(null);
    } catch (err) {
      console.error('Error updating dashboard:', err);
      alert('更新失敗：' + err.message);
    }
  };

  const handleDeleteDashboard = async (dashboardId, dashboardName) => {
    if (!window.confirm(`確定要刪除儀表板「${dashboardName}」嗎？`)) return;

    try {
      await deleteDashboard(dashboardId);
      if (selectedDashboard?.id === dashboardId) {
        setSelectedDashboard(null);
        setCharts([]);
        setChartData({});
      }
      alert('儀表板已刪除');
    } catch (err) {
      console.error('Error deleting dashboard:', err);
      alert('刪除失敗：' + err.message);
    }
  };

  const handleCreateChart = async (e) => {
    e.preventDefault();

    if (!selectedDashboard) {
      alert('請先選擇儀表板');
      return;
    }

    if (!chartForm.name.trim()) {
      alert('請輸入圖表名稱');
      return;
    }

    try {
      const chart = await createChartConfig({
        ...chartForm,
        dashboardId: selectedDashboard.id
      });
      alert('圖表已創建');
      resetChartForm();
      setShowChartModal(false);
      loadDashboardCharts(selectedDashboard.id);
    } catch (err) {
      console.error('Error creating chart:', err);
      alert('創建失敗：' + err.message);
    }
  };

  const handleUpdateChart = async (e) => {
    e.preventDefault();

    if (!editingChart) return;

    try {
      await updateChartConfig(editingChart.id, chartForm);
      alert('圖表已更新');
      resetChartForm();
      setEditingChart(null);
      loadDashboardCharts(selectedDashboard.id);
    } catch (err) {
      console.error('Error updating chart:', err);
      alert('更新失敗：' + err.message);
    }
  };

  const handleDeleteChart = async (chartId, chartName) => {
    if (!window.confirm(`確定要刪除圖表「${chartName}」嗎？`)) return;

    try {
      await deleteChartConfig(chartId);
      alert('圖表已刪除');
      loadDashboardCharts(selectedDashboard.id);
    } catch (err) {
      console.error('Error deleting chart:', err);
      alert('刪除失敗：' + err.message);
    }
  };

  const handleRefreshData = async () => {
    if (!selectedDashboard) return;

    try {
      setRefreshing(true);
      await loadDashboardCharts(selectedDashboard.id);
      alert('資料已更新');
    } catch (err) {
      console.error('Error refreshing data:', err);
      alert('更新失敗：' + err.message);
    } finally {
      setRefreshing(false);
    }
  };

  const handleExportDashboard = () => {
    if (!selectedDashboard) return;
    exportDashboardConfig(selectedDashboard, charts);
  };

  const handleDuplicateDashboard = async (dashboardId) => {
    try {
      const newDashboard = await duplicateDashboard(dashboardId, accountId || 'default');
      alert('儀表板已複製');
      setSelectedDashboard(newDashboard);
    } catch (err) {
      console.error('Error duplicating dashboard:', err);
      alert('複製失敗：' + err.message);
    }
  };

  const resetDashboardForm = () => {
    setDashboardForm({
      name: '',
      description: '',
      refreshInterval: 60
    });
  };

  const resetChartForm = () => {
    setChartForm({
      name: '',
      type: 'bar',
      dataSource: {
        type: 'query',
        queryTemplateId: ''
      },
      config: {
        xAxisField: '',
        yAxisField: '',
        labelField: '',
        valueField: '',
        colors: defaultColors,
        showLegend: true,
        showGrid: true,
        title: '',
        subtitle: ''
      },
      limit: 10
    });
  };

  const renderChart = (chart) => {
    const data = chartData[chart.id] || [];

    if (data.length === 0) {
      return (
        <div className="empty-chart">
          <p>暫無資料</p>
        </div>
      );
    }

    switch (chart.type) {
      case 'stat':
        return renderStatCard(chart, data);
      case 'table':
        return renderTable(chart, data);
      case 'bar':
        return renderBarChart(chart, data);
      case 'pie':
        return renderPieChart(chart, data);
      default:
        return <div className="chart-placeholder">圖表類型: {chart.type}</div>;
    }
  };

  const renderStatCard = (chart, data) => {
    const { valueField } = chart.config;
    const total = data.reduce((sum, item) => sum + Number(item[valueField] || 0), 0);
    const avg = data.length > 0 ? total / data.length : 0;

    return (
      <div className="stat-card-chart">
        <div className="stat-main">
          <div className="stat-value">{total.toFixed(0)}</div>
          <div className="stat-label">總計</div>
        </div>
        <div className="stat-details">
          <div className="stat-item">
            <span>平均</span>
            <span>{avg.toFixed(2)}</span>
          </div>
          <div className="stat-item">
            <span>筆數</span>
            <span>{data.length}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderTable = (chart, data) => {
    if (data.length === 0) return null;

    const fields = Object.keys(data[0]);

    return (
      <div className="table-chart">
        <table>
          <thead>
            <tr>
              {fields.map(field => (
                <th key={field}>{field}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.slice(0, chart.limit || 10).map((row, idx) => (
              <tr key={idx}>
                {fields.map(field => (
                  <td key={field}>{String(row[field] || '-')}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderBarChart = (chart, data) => {
    const { xAxisField, yAxisField } = chart.config;
    const maxValue = Math.max(...data.map(item => Number(item[yAxisField] || 0)));

    return (
      <div className="bar-chart">
        <div className="bars-container">
          {data.slice(0, chart.limit || 10).map((item, idx) => {
            const value = Number(item[yAxisField] || 0);
            const height = maxValue > 0 ? (value / maxValue) * 100 : 0;

            return (
              <div key={idx} className="bar-wrapper">
                <div className="bar-value">{value}</div>
                <div
                  className="bar"
                  style={{
                    height: `${height}%`,
                    backgroundColor: chart.config.colors[idx % chart.config.colors.length]
                  }}
                />
                <div className="bar-label">{String(item[xAxisField] || '-')}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderPieChart = (chart, data) => {
    const { labelField, valueField } = chart.config;
    const total = data.reduce((sum, item) => sum + Number(item[valueField] || 0), 0);

    return (
      <div className="pie-chart">
        <div className="pie-legend">
          {data.slice(0, chart.limit || 10).map((item, idx) => {
            const value = Number(item[valueField] || 0);
            const percentage = total > 0 ? (value / total * 100).toFixed(1) : 0;

            return (
              <div key={idx} className="legend-item">
                <span
                  className="legend-color"
                  style={{ backgroundColor: chart.config.colors[idx % chart.config.colors.length] }}
                />
                <span className="legend-label">{String(item[labelField] || '-')}</span>
                <span className="legend-value">{percentage}%</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="dashboard-builder">
        <div className="loading">載入中...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-builder">
      <header className="page-header">
        <div className="header-left">
          <h1>動態儀表板</h1>
          <p>自定義圖表與數據視覺化</p>
        </div>
        <div className="header-right">
          {selectedDashboard && (
            <>
              <button
                className="btn btn-success"
                onClick={handleRefreshData}
                disabled={refreshing}
              >
                {refreshing ? '更新中...' : '🔄 更新資料'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleExportDashboard}
              >
                📥 匯出設定
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  resetChartForm();
                  setShowChartModal(true);
                }}
              >
                ➕ 新增圖表
              </button>
            </>
          )}
          <button
            className="btn btn-primary"
            onClick={() => {
              resetDashboardForm();
              setShowDashboardModal(true);
            }}
          >
            ➕ 新增儀表板
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/dashboard')}
          >
            返回
          </button>
        </div>
      </header>

      <div className="builder-container">
        {/* Dashboards Sidebar */}
        <div className="dashboards-sidebar">
          <h2>我的儀表板</h2>
          {dashboards.length === 0 ? (
            <div className="empty-dashboards">
              <p>尚無儀表板</p>
              <button
                className="btn btn-sm btn-primary"
                onClick={() => {
                  resetDashboardForm();
                  setShowDashboardModal(true);
                }}
              >
                創建第一個儀表板
              </button>
            </div>
          ) : (
            <div className="dashboards-list">
              {dashboards.map((dashboard) => (
                <div
                  key={dashboard.id}
                  className={`dashboard-item ${selectedDashboard?.id === dashboard.id ? 'active' : ''}`}
                >
                  <div className="dashboard-info" onClick={() => setSelectedDashboard(dashboard)}>
                    <h4>{dashboard.name}</h4>
                    <p>{dashboard.description}</p>
                  </div>
                  <div className="dashboard-item-actions">
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleDuplicateDashboard(dashboard.id)}
                      title="複製"
                    >
                      📋
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeleteDashboard(dashboard.id, dashboard.name)}
                      title="刪除"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Charts Display */}
        <div className="charts-display">
          {!selectedDashboard ? (
            <div className="empty-display">
              <h3>請選擇儀表板</h3>
              <p>在左側選擇或創建儀表板以開始</p>
            </div>
          ) : charts.length === 0 ? (
            <div className="empty-charts">
              <h3>此儀表板尚無圖表</h3>
              <p>點擊「➕ 新增圖表」開始創建</p>
            </div>
          ) : (
            <div className="charts-grid">
              {charts.map((chart) => (
                <div key={chart.id} className="chart-card">
                  <div className="chart-header">
                    <div>
                      <h4>{chart.name}</h4>
                      {chart.config.subtitle && (
                        <p className="chart-subtitle">{chart.config.subtitle}</p>
                      )}
                    </div>
                    <div className="chart-actions">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => {
                          setEditingChart(chart);
                          setChartForm({
                            name: chart.name,
                            type: chart.type,
                            dataSource: chart.dataSource,
                            config: chart.config,
                            limit: chart.limit
                          });
                        }}
                      >
                        ✏️
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteChart(chart.id, chart.name)}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                  <div className="chart-body">
                    {renderChart(chart)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Dashboard Modal */}
      {(showDashboardModal || editingDashboard) && (
        <div className="modal-overlay" onClick={() => {
          setShowDashboardModal(false);
          setEditingDashboard(null);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingDashboard ? '編輯儀表板' : '新增儀表板'}</h2>
              <button className="close-btn" onClick={() => {
                setShowDashboardModal(false);
                setEditingDashboard(null);
              }}>
                ✕
              </button>
            </div>

            <form onSubmit={editingDashboard ? handleUpdateDashboard : handleCreateDashboard} className="dashboard-form">
              <div className="form-group">
                <label>儀表板名稱 *</label>
                <input
                  type="text"
                  value={dashboardForm.name}
                  onChange={(e) => setDashboardForm({ ...dashboardForm, name: e.target.value })}
                  placeholder="例如: 銷售分析儀表板"
                  required
                />
              </div>

              <div className="form-group">
                <label>描述</label>
                <textarea
                  value={dashboardForm.description}
                  onChange={(e) => setDashboardForm({ ...dashboardForm, description: e.target.value })}
                  placeholder="儀表板用途說明"
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>自動更新間隔（秒）</label>
                <input
                  type="number"
                  value={dashboardForm.refreshInterval}
                  onChange={(e) => setDashboardForm({ ...dashboardForm, refreshInterval: parseInt(e.target.value) })}
                  min="10"
                  max="3600"
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => {
                  setShowDashboardModal(false);
                  setEditingDashboard(null);
                }}>
                  取消
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingDashboard ? '更新' : '創建'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Chart Modal */}
      {(showChartModal || editingChart) && (
        <div className="modal-overlay" onClick={() => {
          setShowChartModal(false);
          setEditingChart(null);
        }}>
          <div className="modal-content chart-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingChart ? '編輯圖表' : '新增圖表'}</h2>
              <button className="close-btn" onClick={() => {
                setShowChartModal(false);
                setEditingChart(null);
              }}>
                ✕
              </button>
            </div>

            <form onSubmit={editingChart ? handleUpdateChart : handleCreateChart} className="chart-form">
              <div className="form-section">
                <h3>基本資訊</h3>
                <div className="form-group">
                  <label>圖表名稱 *</label>
                  <input
                    type="text"
                    value={chartForm.name}
                    onChange={(e) => setChartForm({ ...chartForm, name: e.target.value })}
                    placeholder="例如: 月銷售額統計"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>圖表類型 *</label>
                  <select
                    value={chartForm.type}
                    onChange={(e) => setChartForm({ ...chartForm, type: e.target.value })}
                  >
                    {chartTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.icon} {type.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-section">
                <h3>資料來源</h3>
                <div className="form-group">
                  <label>來源類型</label>
                  <select
                    value={chartForm.dataSource.type}
                    onChange={(e) => setChartForm({
                      ...chartForm,
                      dataSource: { ...chartForm.dataSource, type: e.target.value }
                    })}
                  >
                    <option value="query">查詢範本</option>
                    <option value="collection">MongoDB 集合</option>
                  </select>
                </div>

                {chartForm.dataSource.type === 'query' && (
                  <div className="form-group">
                    <label>查詢範本</label>
                    <select
                      value={chartForm.dataSource.queryTemplateId || ''}
                      onChange={(e) => setChartForm({
                        ...chartForm,
                        dataSource: { ...chartForm.dataSource, queryTemplateId: e.target.value }
                      })}
                    >
                      <option value="">選擇查詢範本...</option>
                      {queryTemplates.map(template => (
                        <option key={template.id} value={template.id}>
                          {template.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="form-section">
                <h3>圖表設定</h3>
                {(chartForm.type === 'bar' || chartForm.type === 'line' || chartForm.type === 'area') && (
                  <>
                    <div className="form-group">
                      <label>X 軸欄位</label>
                      <input
                        type="text"
                        value={chartForm.config.xAxisField}
                        onChange={(e) => setChartForm({
                          ...chartForm,
                          config: { ...chartForm.config, xAxisField: e.target.value }
                        })}
                        placeholder="例如: date"
                      />
                    </div>
                    <div className="form-group">
                      <label>Y 軸欄位</label>
                      <input
                        type="text"
                        value={chartForm.config.yAxisField}
                        onChange={(e) => setChartForm({
                          ...chartForm,
                          config: { ...chartForm.config, yAxisField: e.target.value }
                        })}
                        placeholder="例如: amount"
                      />
                    </div>
                  </>
                )}

                {chartForm.type === 'pie' && (
                  <>
                    <div className="form-group">
                      <label>標籤欄位</label>
                      <input
                        type="text"
                        value={chartForm.config.labelField}
                        onChange={(e) => setChartForm({
                          ...chartForm,
                          config: { ...chartForm.config, labelField: e.target.value }
                        })}
                        placeholder="例如: category"
                      />
                    </div>
                    <div className="form-group">
                      <label>數值欄位</label>
                      <input
                        type="text"
                        value={chartForm.config.valueField}
                        onChange={(e) => setChartForm({
                          ...chartForm,
                          config: { ...chartForm.config, valueField: e.target.value }
                        })}
                        placeholder="例如: amount"
                      />
                    </div>
                  </>
                )}

                {chartForm.type === 'stat' && (
                  <div className="form-group">
                    <label>數值欄位</label>
                    <input
                      type="text"
                      value={chartForm.config.valueField}
                      onChange={(e) => setChartForm({
                        ...chartForm,
                        config: { ...chartForm.config, valueField: e.target.value }
                      })}
                      placeholder="例如: amount"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>顯示數量限制</label>
                  <input
                    type="number"
                    value={chartForm.limit}
                    onChange={(e) => setChartForm({ ...chartForm, limit: parseInt(e.target.value) })}
                    min="1"
                    max="100"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => {
                  setShowChartModal(false);
                  setEditingChart(null);
                }}>
                  取消
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingChart ? '更新' : '創建'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardBuilder;
